// MA-C18 — `dot_pattern` renderer for subitizing (ms.math.01, Chapter-1 gate).
//
// THE GAP THIS CLOSES: the subitize slice ("Quick look. How many?", "How many
// do you see?") rendered BLANK — there was no dot-pattern renderer, so the
// mathExemplar dot_card/five_frame/picture_set items (20) and the foundation
// subitizing_pattern items (30) served a question with nothing to look at.
//
// WHAT IT DRAWS: exactly `total` dots (= the count = the answer), arranged by
// FAMILY (the `arrangement` prop):
//   • "dice"    — canonical dice-face pip layout for 1..6 (recognizable "at a
//                 glance" arrangement); >6 falls back to the generic grid.
//   • "frame"   — a 1×5 five-frame with the first `total` cells filled (>5
//                 falls back to generic).
//   • "generic" — a compact dot cluster (also the fallback for any unmapped
//                 layout token). Draws exactly `total` dots.
// Per the MA-C18 decision log: ANSWER-SAFETY (drawn count === expectedAnswer)
// is absolute and holds in every family; arrangement fidelity is by family,
// not per-authored-token. An unmapped/unknown arrangement can only reach the
// generic fallback — still exactly `total` dots, never zero, never a guess.
//
// ANSWER-SAFE by construction (MA-C1 / ADR-012 invariant + the MA-C18 rules):
//   • Draws exactly `total` dot nodes — one <circle> per counted object, so the
//     count-integrity guard can tally the output.
//   • NO <text> numerals anywhere (an SVG numeral would print the answer).
//   • The aria-label NEVER names the count (for subitizing the count IS the
//     answer, so any spoken quantity would read the answer aloud — this is
//     deliberately stricter than the TenFrame/Groups aria labels; see MA-116).
//   • No operator symbol; a single group (no a+b).
//
// ADDITIVE — registers itself under the "dot_pattern" kind (bottom of file).
// The three layout builders are plain factory functions (return an <svg>), so
// the default renderer returns the <svg> DIRECTLY (no nested component layer) —
// the count-integrity + newRenderers guards can walk the element tree.
//
// PROPS (MathRendererProps): `total` = the count to draw (the answer); `a`/`b`
// carried for the sweep contract (a === total, b === 0) but the drawn count is
// keyed off `total`; `arrangement` selects the family; `op`/`glyph` unused.
import type { ReactElement } from "react";
import type { MathRendererProps } from "./rendererRegistry";
import type { DotGroupLayout } from "@/lib/engine/dotGroupLayouts";

const DOT_FILL    = "var(--pal-orange, #d97742)";
const CELL_STROKE = "var(--color-border-secondary, #cdbfae)";
const DOT_R       = 13;

// ── dice-face pip anchors on a 3×3 grid (cols/rows at 30/60/90 in a 120 box) ──
const G = (col: number, row: number) => ({ x: 30 + col * 30, y: 30 + row * 30 });
const TL = G(0, 0), TC = G(1, 0), TR = G(2, 0);
const ML = G(0, 1), C  = G(1, 1), MR = G(2, 1);
const BL = G(0, 2), BC = G(1, 2), BR = G(2, 2);
// Standard die faces. Each entry has EXACTLY that many pips (answer-safe).
const DICE_FACES: Record<number, { x: number; y: number }[]> = {
  1: [C],
  2: [TL, BR],
  3: [TL, C, BR],
  4: [TL, TR, BL, BR],
  5: [TL, TR, C, BL, BR],
  6: [TL, ML, BL, TR, MR, BR],
};

// void-safe positive integer count (the mapper only ever emits a valid count,
// but the renderer must never throw on a stray payload).
function safeCount(total: unknown): number {
  const n = Math.floor(Number(total));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function genericCluster(n: number): ReactElement {
  // Compact grid: up to 5 per row (mirrors the counting Dots layout so a dot
  // here has the same footprint as the ten-frame/groups dot).
  const perRow = Math.min(5, Math.max(1, n));
  const rows = Math.ceil(n / perRow);
  const w = 12 + perRow * 34;
  const h = 12 + rows * 34;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" style={{ maxWidth: Math.min(260, w) }} role="img"
      aria-label="Quick-look dot pattern">
      {Array.from({ length: n }).map((_, i) => (
        <circle key={i} cx={18 + (i % perRow) * 34} cy={18 + Math.floor(i / perRow) * 34} r={DOT_R} fill={DOT_FILL} />
      ))}
    </svg>
  );
}

function diceFace(n: number): ReactElement {
  const pips = DICE_FACES[n];
  if (!pips) return genericCluster(n); // >6: generic fallback (still n dots)
  return (
    <svg viewBox="0 0 120 120" width="100%" style={{ maxWidth: 150 }} role="img"
      aria-label="Quick-look dot card">
      {pips.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={DOT_R} fill={DOT_FILL} />
      ))}
    </svg>
  );
}

function fiveFrame(n: number): ReactElement {
  if (n > 5) return genericCluster(n); // overflow: generic (still n dots)
  return (
    <svg viewBox="0 0 360 80" width="100%" style={{ maxWidth: 360 }} role="img"
      aria-label="Quick-look five frame">
      {Array.from({ length: 5 }).map((_, i) => {
        const x = 6 + i * 70;
        return (
          <g key={i}>
            <rect x={x} y={6} width="68" height="68" rx="8" fill="none" stroke={CELL_STROKE} strokeWidth="2" />
            {i < n && <circle cx={x + 34} cy={40} r="22" fill={DOT_FILL} />}
          </g>
        );
      })}
    </svg>
  );
}

export default function DotPatternRenderer({ total, arrangement }: MathRendererProps): ReactElement {
  const n = safeCount(total);
  if (n <= 0) {
    // Fail-safe: never throw, never draw a misleading zero picture.
    return <svg viewBox="0 0 10 10" width="0" height="0" role="img" aria-label="Quick-look dot pattern" />;
  }
  if (arrangement === "dice") return diceFace(n);
  if (arrangement === "frame") return fiveFrame(n);
  return genericCluster(n);
}

// ── MA-702 — dot-group OPTION TILE glyph ────────────────────────────────────
// A named export (the default renderer above is unchanged) that draws ONE
// answer tile's dot group for the "Match the card to the right group of
// dots" items: exactly `count` dots in the authored `layout`
// (dotGroupLayouts.ts is the shared vocabulary the mapper admits against).
//
// EVERY dot tile draws the SAME outline "plate" (a rounded-square tray
// inset inside the tile) with its dots inside it; ZERO (`empty_circle`) is
// the empty plate — no circle anywhere, so nothing on any tile resembles
// the numeral "0" printed on the card above (MA-702 RED round 1, MF-1: a
// lone beige ring read as a placeholder AND as a copy of the "0" glyph).
// Plate outline and dots are both drawn in the house ink token so they keep
// >= 3:1 contrast on the tile's idle, wrong-tap, correct-tap and reveal
// backgrounds (pinned in ma702DotGroupOptionTiles.test.mjs, which resolves
// DOT_TILE_INK and the tile backgrounds from globals.css).
//
// ANSWER-SAFE / LABEL-SAFE by construction:
//   • no <text> anywhere, no numeral glyph, no data-* attribute;
//   • aria-hidden + no role: the tile BUTTON carries the accessible name
//     ("dot group A", …), never this drawing — the drawing never names a
//     count.
// Every layout fits its viewBox with clearance for the stroke (the
// geometry constants below are exported so a test can measure the markup
// against them, never against re-typed numbers).

/** Colour of every mark on a dot tile (plate outline, cells, dots): the
 *  house ink token (globals.css `--ink`), with its own value as fallback. */
export const DOT_TILE_INK = "var(--ink, #2A1F1A)";
/** The square tile box every fixed-count layout is drawn in. */
export const DOT_GROUP_BOX = 120;
/** The rendered tile-glyph size (CSS px) of the square box. */
export const DOT_GROUP_PX = 64;
/** The plate (rounded-square tray) inside the square box. */
export const PLATE_INSET = 8;
export const PLATE_RX = 20;
export const PLATE_STROKE = 4;
/** `five_frame_aligned` geometry (a 1×5 frame inside a plate, first n
 *  cells filled). */
export const FRAME_W = 380;
export const FRAME_H = 96;
export const FRAME_PX_W = 150;
export const DOT_GROUP_DOT_R = DOT_R;

const FIXED_LAYOUT_POINTS: Record<string, { x: number; y: number }[]> = {
  empty_circle: [],
  single_center_dot: [C],
  horizontal_pair: [{ x: 35, y: 60 }, { x: 85, y: 60 }],
  small_triangle: [{ x: 60, y: 37 }, { x: 34, y: 83 }, { x: 86, y: 83 }],
  four_corners: DICE_FACES[4],
  five_quincunx: DICE_FACES[5],
};

export function DotGroupGlyph({ count, layout }: { count: number; layout: DotGroupLayout }): ReactElement {
  if (layout === "five_frame_aligned") {
    const n = Math.max(0, Math.min(5, Math.floor(count)));
    return (
      <svg viewBox={`0 0 ${FRAME_W} ${FRAME_H}`} width={FRAME_PX_W} height={(FRAME_PX_W * FRAME_H) / FRAME_W}
        // Narrow phones: a 2-column tile can be narrower than FRAME_PX_W, so
        // the frame shrinks with the tile (keeping a 4px side gutter)
        // instead of overflowing it; the viewBox keeps the aspect ratio.
        style={{ width: "calc(100% - 8px)", maxWidth: FRAME_PX_W, height: "auto" }}
        aria-hidden="true" focusable="false" className="mx-auto block">
        <rect x={PLATE_INSET} y={PLATE_INSET} width={FRAME_W - 2 * PLATE_INSET} height={FRAME_H - 2 * PLATE_INSET}
          rx={PLATE_RX} fill="none" stroke={DOT_TILE_INK} strokeWidth={PLATE_STROKE} />
        {Array.from({ length: 5 }).map((_, i) => {
          const x = 16 + i * 70;
          return (
            <g key={i}>
              <rect x={x} y={14} width="68" height="68" rx="8" fill="none" stroke={DOT_TILE_INK} strokeWidth="2" />
              {i < n && <circle cx={x + 34} cy={48} r="22" fill={DOT_TILE_INK} />}
            </g>
          );
        })}
      </svg>
    );
  }
  const points = FIXED_LAYOUT_POINTS[layout] ?? [];
  return (
    <svg viewBox={`0 0 ${DOT_GROUP_BOX} ${DOT_GROUP_BOX}`} width={DOT_GROUP_PX} height={DOT_GROUP_PX}
      aria-hidden="true" focusable="false" className="mx-auto block">
      <rect x={PLATE_INSET} y={PLATE_INSET} width={DOT_GROUP_BOX - 2 * PLATE_INSET} height={DOT_GROUP_BOX - 2 * PLATE_INSET}
        rx={PLATE_RX} fill="none" stroke={DOT_TILE_INK} strokeWidth={PLATE_STROKE} />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={DOT_R} fill={DOT_TILE_INK} />
      ))}
    </svg>
  );
}
